import { useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Config/Config';
import { Mail, DollarSign } from 'lucide-react';
import LoaderSpinner from '../Essentials/LoaderSpinner';
import logo from '../../assets/finance7.png';

const Resetpwd = () => {
  const [email, setEmail] = useState('');
  const [lastDeposit, setLastDeposit] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRetrievePassword = async () => {
    setError('');
    setPassword('');
    setIsLoading(true);

    if (!email || !lastDeposit) {
      setError('Please fill in all fields.');
      setIsLoading(false);
      return;
    }

    try {
      const forgetPasswordRef = doc(db, 'forgetpassword', email);
      const forgetPasswordSnap = await getDoc(forgetPasswordRef);
      
      if (!forgetPasswordSnap.exists()) {
        setError('No record found for this email.');
        setIsLoading(false);
        return;
      }

      const userData = forgetPasswordSnap.data();
      const uid = userData.uid;
      const transHistoryRef = doc(db, 'TransHistory', uid);
      const transHistorySnap = await getDoc(transHistoryRef);

      if (!transHistorySnap.exists()) {
        setError('Transaction history not found.');
        setIsLoading(false);
        return;
      }

      const transData = transHistorySnap.data();
      if (!transData.depositTrans || !Array.isArray(transData.depositTrans)) {
        setError('No deposit history found.');
        setIsLoading(false);
        return;
      }

      const sortedDeposits = [...transData.depositTrans].sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      
      const mostRecentDeposit = sortedDeposits[0];
      
      const lastDepositAmount = String(lastDeposit);
      const recentDepositAmount = String(mostRecentDeposit.amount);
      
      if (!mostRecentDeposit || recentDepositAmount !== lastDepositAmount) {
        setError('Last deposit amount does not match our records.');
        setIsLoading(false);
        return;
      }

      setPassword(userData.password);
    } catch (err) {
      setError('Error retrieving password. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-[#000300] py-16 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center relative z-[1100]"
      style={{ paddingTop: 'calc(2rem + 2px)' }}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-32 h-32 bg-[#00df9a] opacity-10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-[#00df9a] opacity-10 rounded-full filter blur-3xl"></div>
      </div>
      
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden z-10">
        {/* Premium Header with gradient */}
        <div className="bg-[#000300] py-6 px-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#00df9a] rounded-full filter blur-xl"></div>
          </div>
          <div className="relative z-10">
            <img src={logo} alt="Company Logo" className="h-8 sm:h-9 md:h-10 lg:h-10 mx-auto mb-3" />
            <p className="text-xs sm:text-sm md:text-md lg:text-md text-gray-300 mt-1">Recover your account password</p>
          </div>
        </div>

        <div className="p-8 md:p-10">
          <form className="space-y-6">
            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 tracking-wide">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="block w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#000300] focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* Last Deposit */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 tracking-wide">Last Deposit Amount</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Enter amount (e.g. 500)"
                  value={lastDeposit}
                  onChange={(e) => setLastDeposit(e.target.value)}
                  required
                  className="block w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#000300] focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="button"
                onClick={handleRetrievePassword}
                disabled={isLoading}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#000300] hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#000300] transition-all duration-200 ${
                  isLoading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? <LoaderSpinner /> : 'Retrieve Password'}
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-100 shadow-sm">
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          )}

          {password && (
            <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-100 shadow-sm">
              <p className="text-green-700 font-medium">
                Your password is: <strong>{password}</strong>
              </p>
              <p className="text-sm text-green-600 mt-2">
                For security reasons, please change your password after logging in.
              </p>
            </div>
          )}

          <div className="mt-6 text-center text-sm">
            <p className="text-gray-600">
              Remember your password?{' '}
              <a href="/login" className="text-[#000300] hover:text-gray-800 font-medium underline">
                Sign in
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resetpwd;